import { useT } from '@/shared/i18n';
import type { ReactNode } from 'react';

/**
 * HeroMockup — a self-contained, code-drawn preview of the Workerize product
 * for the two-column hero (no image file, CSP-safe). Built entirely from theme
 * tokens so it adapts to light/dark, and from logical properties (`ms/me`,
 * start/end) so it mirrors correctly in RTL.
 *
 * Depth: a faint tilted panel sits behind the frame, a soft lime glow sits
 * behind that, and the whole stack drifts with a slow idle float (`wk-float`,
 * disabled under reduced-motion). A small "live" chip overlaps the top-end
 * corner for the premium SaaS feel.
 *
 * It is decorative: the outer wrapper carries a single `role="img"` + label
 * (`landing.mockup.alt`) and everything inside is `aria-hidden`. The figures
 * (87, 92%, bars) are representative chrome — NOT product claims (DEC-051).
 */
export function HeroMockup(): ReactNode {
  const { t } = useT();

  // Window traffic-light dots use fixed hex (independent of the Tailwind
  // palette) so they render identically in every theme.
  const dots = ['#ff5f57', '#febc2e', '#28c840'];
  // Skill rows: language-neutral tech tokens, so no translation churn.
  const skills: Array<[string, number]> = [
    ['React', 92],
    ['Python', 78],
    ['SQL', 64],
  ];
  // Momentum bars — explicit px heights (percentage heights collapse inside a
  // flex row), trending upward.
  const trend = [16, 22, 19, 28, 25, 34, 30, 38];

  return (
    <div
      role="img"
      aria-label={t('landing.mockup.alt')}
      className="relative w-full max-w-md wk-float"
    >
      {/* Soft lime glow. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -inset-6 rounded-[2.5rem] bg-secondary-fixed/10 blur-3xl"
      />
      {/* Faint tilted panel behind the frame for depth. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -rotate-3 rounded-2xl border border-outline/10 bg-surface-container-high/60"
      />

      <div
        aria-hidden="true"
        className="relative overflow-hidden rounded-2xl border border-outline/20 bg-surface-container-low shadow-2xl shadow-black/20"
      >
        {/* Window chrome */}
        <div className="flex items-center gap-2 border-b border-outline/15 bg-surface-container-high px-4 py-3">
          {dots.map((c) => (
            <span
              key={c}
              className="h-3 w-3 rounded-full"
              style={{ backgroundColor: c }}
            />
          ))}
          <span className="ms-3 text-xs font-semibold tracking-wide text-on-surface-variant">
            workerize.ai
          </span>
        </div>

        {/* Body: mini nav rail + content */}
        <div className="grid grid-cols-[56px_1fr]">
          <div className="flex flex-col items-center gap-4 border-e border-outline/15 bg-surface-container py-5">
            <span className="h-7 w-7 rounded-lg bg-secondary-fixed" />
            {[0, 1, 2, 3].map((i) => (
              <span key={i} className="h-2.5 w-7 rounded-full bg-on-surface/10" />
            ))}
          </div>

          <div className="space-y-3 p-4">
            {/* Profile header row */}
            <div className="flex items-center gap-3">
              <span className="h-9 w-9 shrink-0 rounded-full bg-gradient-to-br from-secondary-fixed to-secondary-fixed/40" />
              <div className="flex-1 space-y-1.5">
                <span
                  className="block h-2.5 w-24 rounded-full bg-on-surface-variant"
                  style={{ opacity: 0.65 }}
                />
                <span
                  className="block h-2 w-16 rounded-full bg-on-surface-variant"
                  style={{ opacity: 0.35 }}
                />
              </div>
              <span className="h-2.5 w-2.5 rounded-full bg-secondary-fixed" />
            </div>

            {/* Employability score + momentum bars */}
            <div className="rounded-xl border border-outline/15 bg-surface-base p-4">
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-on-surface-variant">
                  {t('landing.hero.mockup.score')}
                </p>
                <span className="rounded-full bg-secondary-fixed/15 px-2 py-0.5 text-[11px] font-semibold text-[var(--ink-accent)]">
                  ↑ 12%
                </span>
              </div>
              <div className="mt-1 flex items-baseline gap-1">
                <span className="text-3xl font-bold leading-none text-on-surface">
                  87
                </span>
                <span className="text-sm font-medium text-on-surface-variant">
                  / 100
                </span>
              </div>
              <div className="mt-3 flex h-10 items-end gap-1.5">
                {trend.map((h, i) => (
                  <span
                    key={i}
                    className="flex-1 rounded-sm bg-secondary-fixed"
                    style={{
                      height: `${h}px`,
                      opacity: 0.5 + (i / (trend.length - 1)) * 0.5,
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Top skills */}
            <div className="rounded-xl border border-outline/15 bg-surface-base p-4">
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-on-surface-variant">
                {t('landing.hero.mockup.skills')}
              </p>
              <ul className="space-y-2.5">
                {skills.map(([name, val]) => (
                  <li key={name} className="flex items-center gap-3">
                    <span className="w-14 text-xs font-medium text-on-surface">
                      {name}
                    </span>
                    <span className="relative h-1.5 flex-1 overflow-hidden rounded-full">
                      <span
                        className="absolute inset-0 rounded-full bg-on-surface-variant"
                        style={{ opacity: 0.18 }}
                      />
                      <span
                        className="absolute inset-y-0 start-0 rounded-full bg-secondary-fixed"
                        style={{ width: `${val}%` }}
                      />
                    </span>
                    <span className="w-8 text-end text-[11px] font-semibold tabular-nums text-on-surface-variant">
                      {val}%
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Career match */}
            <div className="flex items-center justify-between rounded-xl border border-outline/15 bg-surface-base px-4 py-3">
              <span className="text-xs font-semibold text-on-surface-variant">
                {t('landing.hero.mockup.match')}
              </span>
              <span className="rounded-full bg-secondary-fixed px-2.5 py-1 text-xs font-bold text-on-secondary-fixed">
                92%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Floating "live" chip overlapping the top-end corner. */}
      <div
        aria-hidden="true"
        className="absolute -top-3 end-4 flex items-center gap-2 rounded-full border border-outline/20 bg-surface-container-high px-3 py-1.5 shadow-lg shadow-black/10"
      >
        <span className="h-2 w-2 animate-pulse rounded-full bg-secondary-fixed" />
        <span className="text-[11px] font-semibold text-on-surface">
          {t('landing.hero.mockup.live')}
        </span>
      </div>
    </div>
  );
}

export default HeroMockup;
